<template>
  <div class="product-card" v-if="order != null">
    <div class="product-card-info">
      <!-- Image Section - Shows all products in batch -->
      <div class="product-image-container" @click="$emit('show')">
        <!-- Single Product -->
        <div v-if="order.order_products.length === 1" class="product-image">
          <img :src="getProductImage(order.order_products[0].product)" alt="Product" />
        </div>
        
        <!-- Multiple Products (2-4 products) -->
        <div v-else class="product-images-grid" :class="getGridClass()">
          <div 
            v-for="(orderProduct) in displayedProducts" 
            :key="orderProduct.id"
            class="grid-image"
          >
            <img :src="getProductImage(orderProduct.product)" :alt="orderProduct.product.name" />
          </div>
          <!-- More badge if more than 4 products -->
          <div v-if="order.order_products.length > 4" class="more-badge">
            +{{ order.order_products.length - 4 }}
          </div>
        </div>

        <!-- Status Badge -->
        <div v-if="order.status === 'sold' || order.status === 'received'" class="top-badge badge-accepted">
          <span>{{ $t('sentence.sold') }}</span>
        </div>
      </div>

      <div class="product-info">
        <!-- Order ID and Date -->
        <h4 @click="$emit('show')" class="product-name">
          {{ $t('ecommerce.order') }} #{{ order.id }}
          <span v-if="order.order_products.length > 1" class="product-count">
            ({{ order.order_products.length }} {{ $t('sidebar.products') }})
          </span>
        </h4>

        <!-- Product Names List -->
        <div class="products-list">
          <p v-for="orderProduct in order.order_products" :key="orderProduct.id" class="product-item-name">
            • {{ orderProduct.product.name }} (x{{ orderProduct.quantity }})
          </p>
        </div>

        <!-- Price Section -->
        <div class="price-section" @click="$emit('show')">
          <p class="product-price price mb-1">
            {{ order.total_amount | currency(" DA", 0, { symbolOnLeft: false }) }}
          </p>
        </div>

        <!-- Order Date -->
        <div v-if="$route.name === 'myOperations' || $route.name === 'dashboardBase'" class="d-flex created_at">
          <div v-if="order.status === 'onHold' || order.status === 'in_progress'" class="created_at">
            {{ $t('attr.ordered') }} {{ order.created_at | formatDate }}
          </div>
          <div v-else class="created_at">
            {{ $t('attr.received') }} {{ order.created_at | formatDate }}
          </div>
        </div>

        <!-- Rating Section -->
        <div v-if="($route.name === 'myOperations' || $route.name === 'dashboardBase') && showRatingButton"
          class="rating-container mt-2">
          <!-- Display existing rating -->
          <div v-if="hasRating" class="rating-display">
            <div class="stars">
              <i v-for="star in 5" :key="star" class="mdi" :class="getStarClass(star)"></i>
            </div>
            <span class="rating-text">{{ averageRating.toFixed(1) }}</span>
          </div>

          <!-- Rate button when no rating exists -->
          <button v-else class="btn btn-rating" @click="$emit('rate')">
            <i class="mdi mdi-star"></i>
            <span>{{ $t('action.rate') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- STATS AT BOTTOM -->
    <div class="stats-wrapper" v-if="showStats">
      <div class="stats-item">
        <i class="mdi mdi-package-variant"></i>
        <span>{{ totalQuantity }} {{ $t('ecommerce.items') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import config from "@/config.js";

export default {
  name: "OrderCard",
  props: {
    order: {
      type: Object,
      required: true
    },
    showStats: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      storageUrl: config.storageUrl,
    };
  },
  computed: {
    // Show rating button only for completed orders
    showRatingButton() {
      if (!this.order) return false;
      return ['received', 'completed', 'sold'].includes(this.order.status);
    },

    // Check if any product has been rated
    hasRating() {
      if (!this.order?.order_products) return false;
      return this.order.order_products.some(op => op.product?.my_rate?.rating > 0);
    },

    // Calculate average rating across all products
    averageRating() {
      if (!this.order?.order_products) return 0;
      
      const ratings = this.order.order_products
        .map(op => op.product?.my_rate?.rating || 0)
        .filter(r => r > 0);
      
      if (ratings.length === 0) return 0;
      
      return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
    },

    // Display maximum 4 products in grid
    displayedProducts() {
      if (!this.order?.order_products) return [];
      return this.order.order_products.slice(0, 4);
    },

    // Total quantity of all products
    totalQuantity() {
      if (!this.order?.order_products) return 0;
      return this.order.order_products.reduce((sum, op) => sum + op.quantity, 0);
    }
  },
  methods: {
    getProductImage(product) {
      if (!product) return "";
      
      const pics = product.pictures;
      const pic = Array.isArray(pics) && pics.length ? pics[0] : pics || "";
      
      return this.storageUrl + pic;
    },

    getGridClass() {
      const count = this.order.order_products.length;
      if (count === 2) return 'grid-2';
      if (count === 3) return 'grid-3';
      return 'grid-4'; // 4 or more
    },

    getStarClass(position) {
      const rating = this.averageRating;
      if (position <= Math.floor(rating)) {
        return 'mdi-star rating-star-filled';
      } else if (position === Math.ceil(rating) && rating % 1 !== 0) {
        return 'mdi-star-half-full rating-star-half';
      } else {
        return 'mdi-star-outline rating-star-empty';
      }
    }
  },
  filters: {
    formatDate(value) {
      return moment(value).format("DD MMM YYYY");
    }
  }
};
</script>

<style scoped>
.product-card {
  background-color: white;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 2px 8px rgba(14, 21, 27, 0.04);
  cursor: pointer;
  transition: box-shadow 0.18s ease, transform 0.12s ease;
  position: relative;
}

.product-card-info {
  display: flex;
  align-items: center;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(14, 21, 27, 0.08);
}

/* Single Product Image */
.product-image-container {
  width: 164px;
  height: 164px;
  margin-right: 14px;
  flex-shrink: 0;
  position: relative;
}

.product-image {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
  background-color: #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Multiple Products Grid */
.product-images-grid {
  width: 100%;
  height: 100%;
  display: grid;
  gap: 4px;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.product-images-grid.grid-2 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
}

.product-images-grid.grid-3 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.product-images-grid.grid-3 .grid-image:first-child {
  grid-row: 1 / 3;
}

.product-images-grid.grid-4 {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.grid-image {
  width: 100%;
  height: 100%;
  background-color: #f2f3f5;
  overflow: hidden;
  position: relative;
}

.grid-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.more-badge {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  backdrop-filter: blur(4px);
}

.top-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1;
}

.badge-accepted {
  background-color: var(--success);
  color: white;
}

/* Product Info */
.product-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.product-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
}

.product-count {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--gray-600);
}

.products-list {
  max-height: 80px;
  overflow-y: auto;
  margin: 4px 0;
  scrollbar-width: thin;
}

.product-item-name {
  font-size: 0.85rem;
  color: var(--gray-700);
  margin: 2px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-section {
  margin-top: 4px;
}

.product-price {
  font-size: 0.95rem;
  color: var(--color-1);
  font-weight: 600;
}

.created_at {
  font-size: 10pt;
  margin-bottom: 4px;
}

/* Rating Section */
.rating-container {
  margin-top: 0.5rem;
}

.rating-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 8px;
  width: 130px;
}

.stars {
  display: flex;
  gap: 0.15rem;
}

.stars i {
  font-size: 1.1rem;
  transition: transform 0.2s ease;
}

.rating-star-filled {
  color: var(--warning);
}

.rating-star-half {
  color: var(--warning);
}

.rating-star-empty {
  color: var(--gray-500);
}

.rating-text {
  font-weight: 600;
  color: var(--warning-200);
  font-size: 1rem;
}

.btn-rating {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, var(--warning), var(--warning-200));
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(255, 193, 7, 0.3);
}

.btn-rating:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(255, 193, 7, 0.4);
}

.btn-rating i {
  font-size: 1.1rem;
}

/* Stats */
.stats-wrapper {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--gray-200);
}

.stats-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--gray-700);
  font-size: 0.85rem;
}

.stats-item i {
  color: var(--color-nz);
  font-size: 1.1rem;
}

/* Responsive */
@media (max-width: 640px) {
  .product-image-container {
    width: 80px;
    height: 140px;
  }

  .product-name {
    font-size: 0.9rem;
  }

  .product-item-name {
    font-size: 0.8rem;
  }

  .products-list {
    max-height: 60px;
  }
}
</style>